import { type FC, type MouseEvent } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import { connect } from 'react-redux';
import * as tracking from 'dibs-tracking';
import { removeLocaleSegment } from 'dibs-intl/exports/urls';

import { Breadcrumbs } from 'dibs-elements/exports/Breadcrumbs.tsx';
import { defineMessages, useIntl } from 'dibs-react-intl';

import { updateUriRef as updateUriRefAction } from '../../actions/filterActions.ts';
import { getAppliedFilter } from '../SbSharedRefineMenu/sbSharedRefineMenuHelpers.ts';

import {
    checkRewardedSeller,
    checkTradeUserUnderTopLevelLoyaltyTiers,
} from 'dibs-buyer-layout/exports/sellerBrandingHelpers.ts';

// constants
import { pageTypeConstants as PAGE_TYPES } from '../../constants/pageTypeConstants.ts';
import { type DefaultReducersReduxState } from '../../reducers/useSbSelector.ts';

import { type SbSharedPageBreadcrumbs_itemSearch$data } from './__generated__/SbSharedPageBreadcrumbs_itemSearch.graphql.ts';
import { type SbSharedPageBreadcrumbs_user$data } from './__generated__/SbSharedPageBreadcrumbs_user.graphql.ts';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';

// some breadcrumbs are not search / browse pages, but special landing pages instead :
const specialCaseLinks = [
    'creators',
    'collections',
    'designs',
    'seller-directory',
    'infogallery',
    'contemporary',
    // home page
    '',
];

const messages = defineMessages({
    backToItem: {
        id: 'SbSharedPageBreadcrumbs.backToItem',
        defaultMessage: '< Back To Item',
    },
});

const testSpecialCase = (path: string): boolean => {
    const segments = path.split('/').filter(Boolean);

    // check for /creators/{vertical}/ or /creators/{creator}/
    const isCreatorsSpecialCase = segments.length === 2 && segments[0] === 'creators';
    return specialCaseLinks.includes(path.replace(/\//g, '')) || isCreatorsSpecialCase;
};

type Props = {
    itemSearch: SbSharedPageBreadcrumbs_itemSearch$data;
    user: SbSharedPageBreadcrumbs_user$data;
    isClient: boolean;
    updateUriRef: typeof updateUriRefAction;
};

const SbSharedPageBreadcrumbsComponent: FC<Props> = ({
    itemSearch,
    user,
    isClient,
    updateUriRef,
}) => {
    const { pageType, appliedFilters, seller } = itemSearch || {};
    const isMoreFromSeller = pageType === PAGE_TYPES.MORE_FROM_SELLER;
    const itemPk = getAppliedFilter(appliedFilters, 'itemPk')?.values?.[0]?.urlLabel;
    const { formatMessage } = useIntl();

    function fireTracking(event: MouseEvent): void {
        const breadcrumbs = itemSearch?.breadcrumbs;
        const lastBreadcrumb = breadcrumbs?.[breadcrumbs.length - 1];
        const currentCategoryName = lastBreadcrumb?.text;

        tracking.trackEvent(
            {
                category: 'navigation',
                action: 'breadcrumb click',
                label: `${currentCategoryName}|${(event.target as HTMLAnchorElement).text}`,
            },
            event
        );
    }

    function updateRoute(event: MouseEvent): void {
        const path = removeLocaleSegment((event.target as HTMLAnchorElement).pathname);
        const shouldUpdateRoute =
            !testSpecialCase(path) &&
            // more from seller links directly back to PDP and shouldn't be
            // caught by SB router
            !isMoreFromSeller;

        if (shouldUpdateRoute) {
            event.preventDefault();
            fireTracking(event);
            if (updateUriRef) {
                updateUriRef({ uriRef: path });
            }
        }
    }

    let breadcrumbs = (itemSearch?.breadcrumbs || []).filter(filterNulls);

    if (isMoreFromSeller && itemPk) {
        breadcrumbs = [
            {
                text: formatMessage(messages.backToItem),
                path: `/id-${itemPk}`,
                isLinkable: true,
            },
        ];
    }

    if (pageType === PAGE_TYPES.DEALER) {
        breadcrumbs =
            isClient &&
            (checkRewardedSeller(seller) || checkTradeUserUnderTopLevelLoyaltyTiers(user || null))
                ? breadcrumbs
                : [];
    }

    return (
        <Breadcrumbs
            breadcrumbs={breadcrumbs}
            onClick={updateRoute}
            isLastItemActive={isMoreFromSeller}
        />
    );
};

const mapStateToProps = (state: DefaultReducersReduxState): { isClient: boolean } => ({
    isClient: state.relayVariables.variables.isClient,
});

export const SbSharedPageBreadcrumbs = createFragmentContainer(
    connect(mapStateToProps, { updateUriRef: updateUriRefAction })(
        SbSharedPageBreadcrumbsComponent
    ),
    {
        itemSearch: graphql`
            fragment SbSharedPageBreadcrumbs_itemSearch on ItemSearchQueryConnection {
                seller {
                    ...sellerBrandingHelpers_seller
                }
                pageType
                appliedFilters {
                    name
                    values {
                        urlLabel
                    }
                }
                breadcrumbs {
                    text
                    path
                    isLinkable
                }
            }
        `,
        user: graphql`
            fragment SbSharedPageBreadcrumbs_user on User {
                ...sellerBrandingHelpers_user
            }
        `,
    }
);
